import { SelectOption } from 'components/inputs/BaseSelect';
import DateDropdown from 'components/inputs/dropdowns/DateDropdown';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import Label from 'components/inputs/Label';
import ModalSidebar from 'components/modals/sidebar/ModalSidebar';
import ModalSidebarHeader from 'components/modals/sidebar/ModalSidebarHeader';
import Immutable from 'immutable';
import React, { useState } from 'react';
import { Project } from 'records/Project';
import { Submittal } from 'records/Submittal';
import * as toast from 'utils/toast';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { SubmittalStatus } from 'records/SubmittalStatus';
import { useTranslator } from '@bluebeam/react-i18n';
import SubmittalSidebarFooter from './SubmittalSidebarFooter';

interface Props extends WithAppStoreProps {
  submittal: Submittal;
  onBackClick: () => void;
  onAssign: () => void;
}

const AssignSidebar = (props: Props) => {
  const { actions, appState } = props;
  const [updatingSubmittal, setUpdatingSubmittal] = useState<boolean>(false);
  const [nextSubmittal, setNextSubmittal] = useState<Submittal>(
    props.submittal.delete('status').delete('ballincourt')
  );

  const { translate } = useTranslator([
    'buttons',
    'statusLabels',
    'formLabels',
    'toastText',
  ]);

  const handleSubmittalStatusChange = (kind: string): void => {
    const { submittalStatuses } = appState;

    const status = submittalStatuses.find((ss) => ss.kind === kind);

    if (status) {
      setNextSubmittal(nextSubmittal.set('status', status));
    }
  };

  const handleAssigneeChange = (assignee: string): void => {
    setNextSubmittal(nextSubmittal.set('ballincourt', assignee));
  };

  const assigneesToSelectOptions = (): SelectOption[] => {
    const { projects, selectedProject } = appState;
    const project = projects.get(selectedProject, new Project());

    return project.assignees.map((a) => ({ label: a, value: a })).toArray();
  };

  const handleDueDateChanged = (date: Date): void => {
    const subRequested =
      nextSubmittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subDesignReview =
      nextSubmittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';

    if (subRequested) {
      setNextSubmittal(
        nextSubmittal.set('duefromsub', Math.floor(date.getTime() / 1000))
      );
    }

    if (subDesignReview) {
      setNextSubmittal(
        nextSubmittal.set('duefromreviewer', Math.floor(date.getTime() / 1000))
      );
    }
  };

  const getDueDate = (): number | null => {
    const subRequested =
      nextSubmittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subDesignReview =
      nextSubmittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';

    if (subRequested && nextSubmittal.duefromsub > 0) {
      return nextSubmittal.duefromsub;
    }

    if (subDesignReview && nextSubmittal.duefromreviewer > 0) {
      return nextSubmittal.duefromreviewer;
    }

    return null;
  };

  const duedateDisabled = (): boolean => {
    const subRequested =
      nextSubmittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subDesignReview =
      nextSubmittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';

    return !subRequested && !subDesignReview;
  };

  const handleAssignSubmittal = async (): Promise<void> => {
    const { onAssign } = props;
    try {
      setUpdatingSubmittal(true);
      await actions.updateSubmittal(nextSubmittal, Immutable.List());
      onAssign();
    } catch (err) {
      console.error('Unable to assign submittal', err);
      toast.error(
        translate('toastText:submittalAssignError.message'),
        translate('toastText:submittalAssignError.title')
      );
    }
  };

  const { onBackClick } = props;
  const { submittalStatuses } = appState;

  const translatedStatuses = submittalStatuses.map((status) => {
    const statusType: `${string}:${string}` =
      `statusLabels:submittals.${status.kind}` as `${string}:${string}`;

    return new SubmittalStatus({
      kind: status.kind,
      display: translate(statusType),
    });
  });

  const statuses = translatedStatuses.filter(
    (s) =>
      s.kind === 'SUB_SUBMITTAL_REQUESTED' ||
      s.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW'
  );
  return (
    <ModalSidebar id="sub-viewer-modal-assign-sidebar">
      <ModalSidebarHeader
        title={translate('buttons:assign')}
        onBackClick={onBackClick}
      />
      <div className="grow-1 p4">
        <div className="mb3">
          <Label label={translate('statusLabels:status')} required />
          <SingleSelect
            id="sub-viewer-modal-assign-sidebar-status"
            placeholder={translate('formLabels:selectWithDots')}
            value={translatedStatuses.reduce<SelectOption | null>((acc, sc) => {
              if (sc.kind !== nextSubmittal.status.kind) {
                return acc;
              }

              return sc.toSelectOption();
            }, null)}
            options={statuses.map((sc) => sc.toSelectOption()).toArray()}
            onChange={(sc: SelectOption): void => {
              handleSubmittalStatusChange(sc.value);
            }}
          />
        </div>
        <div className="mb3">
          <Label label={translate('formLabels:assignee')} required />
          <SingleSelect
            id="submittal-viewer-assign-sidebar-assignee"
            placeholder={translate('formLabels:selectWithDots')}
            value={{
              label: nextSubmittal.ballincourt,
              value: nextSubmittal.ballincourt,
            }}
            options={assigneesToSelectOptions()}
            onChange={(sc): void => {
              handleAssigneeChange(sc.value);
            }}
          />
        </div>
        <div className="mb3">
          <Label label={translate('formLabels:duedate')} required />
          <DateDropdown
            id="submittal-viewer-assign-sidebar-duedate"
            date={getDueDate()}
            onChange={handleDueDateChanged}
            disabled={duedateDisabled()}
          />
        </div>
      </div>
      <SubmittalSidebarFooter
        id="submittal-viewer-assign-sidebar-footer"
        confirmText={translate('buttons:send')}
        confirmDisabled={updatingSubmittal}
        onConfirmClick={handleAssignSubmittal}
      />
    </ModalSidebar>
  );
};

export default withAppStore<Props>(AssignSidebar);
